/**
 * Bind the Property Dossier template to the pipeline Loan the operator already
 * has on screen. GET /loans/{id} is an overlay, not a gate: a 404 must not
 * blank the chrome.
 */
import type { QueryClient } from '@tanstack/react-query'
import { COMMAND_CENTER_LOANS } from '@/data/commandCenterLoans'
import type { RawDealFile } from '@/services/dealFile'
import type { Loan } from '@/types'

function defined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

/**
 * Pipeline Loan percentages use decimal ratios (0.598), while the deal-file
 * catalogue and its derivation rules use percentage points (59.8). Normalize
 * at this boundary so dossier display, editing, and derived values share one
 * scale without changing the canonical Loan contract.
 */
function percentPoints(value: number | null | undefined): number | null | undefined {
  if (!defined(value)) return value
  return Math.abs(value) <= 1.5 ? value * 100 : value
}

/** Pipeline Loan (camelCase) → the snake_case deal-file shape Overview/Record read. */
export function loanToDealFile(loan: Loan): RawDealFile {
  const cash = loan.cashFlows
  const market = loan.market
  const custom = { ...(loan.customFields ?? {}) }

  return {
    id: loan.id,
    property_name: loan.property,
    asset_type: loan.type,
    location: loan.location,
    borrower: loan.borrower,
    sponsor: loan.sponsor,
    sponsor_credit: loan.sponsorCredit,
    loan_amount: loan.loanAmount,
    funded_amount: loan.fundedAmount ?? null,
    funded_at: loan.fundedAt ?? null,
    broker_name: loan.brokerName ?? null,
    broker_id: loan.brokerId ?? null,
    source_ingestion_id: loan.sourceIngestionId ?? null,
    ltv: percentPoints(loan.ltv),
    dscr: loan.dscr,
    debt_yield: percentPoints(loan.debtYield),
    stage: loan.stage,
    stage_index: loan.stageIndex,
    phase: loan.phase,
    assignee: loan.assignee,
    days_open: loan.daysOpen,
    days_in_status: loan.daysInStatus ?? null,
    total_days_open: loan.totalDaysOpen ?? null,
    maturity: loan.maturity,
    rate: loan.rate,
    originated: loan.originated,
    created_at: loan.createdAt ?? null,
    priority: loan.priority,
    product: loan.product,
    property_value: loan.propertyValue,
    noi: loan.noi,
    sf: loan.sf,
    units: loan.units,
    year_built: loan.yearBuilt,
    cash_flows: cash,
    market_data: market,
    rent_roll: loan.rentRoll,
    // Columns the API has always returned and this mapper used to drop, so a
    // pipeline row with no persisted deal file lost its written sections, its
    // capital source and its master-record links on the way to the dossier.
    narratives: loan.narratives ?? null,
    investor: loan.investor ?? null,
    originator: loan.originator ?? null,
    capital_program: loan.capitalProgram ?? loan.product ?? null,
    property_id: loan.propertyId ?? null,
    sponsor_id: loan.sponsorId ?? null,
    deal_status: loan.dealStatus ?? null,
    dead_reason: loan.deadReason ?? null,
    funding_notes: loan.fundingNotes ?? null,
    capital_stack: loan.capitalStack?.map((tranche) => ({
      ...tranche,
      ltv: percentPoints(tranche.ltv) ?? tranche.ltv,
    })),
    notes: loan.notes,
    killed_at: loan.killedAt ?? null,
    killed_at_stage: loan.killedAtStage ?? null,
    // Catalogue lookups fall back to raw[name] when defs are missing. The
    // deal's cap rate is never read out of `market.capRate`: that key is a
    // market source's figure, and filing it as the deal's own cap rate is the
    // same confusion as printing the deal's cap rate as "Market Cap Rate".
    expense_ratio: percentPoints(cash?.expenseRatio),
    gross_revenue: cash?.revenue,
    operating_expenses: cash?.expenses,
    // NCF is not NOI. Mapping prospectiveNOI here made every deal without a
    // catalogue ncf show the in-place NOI as net cash flow.
    ncf: cash?.ncf ?? custom.ncf,
    custom_fields: {
      ...custom,
      ...(defined(loan.debtYield) && custom.debt_yield === undefined
        ? { debt_yield: percentPoints(loan.debtYield) }
        : {}),
      ...(defined(cash?.expenseRatio) && custom.expense_ratio === undefined
        ? { expense_ratio: percentPoints(cash.expenseRatio) }
        : {}),
      ...(defined(cash?.revenue) && custom.gross_revenue === undefined
        ? { gross_revenue: cash.revenue }
        : {}),
      ...(defined(cash?.expenses) && custom.operating_expenses === undefined
        ? { operating_expenses: cash.expenses }
        : {}),
    },
  }
}

/** API deal-file on top of the pipeline row. Overlay wins per key; custom_fields merge. */
export function mergeDealFile(
  base: RawDealFile | undefined,
  overlay: RawDealFile | undefined,
): RawDealFile | undefined {
  if (!base && !overlay) return undefined
  if (!overlay) return base
  if (!base) return overlay
  const baseCustom =
    base.custom_fields && typeof base.custom_fields === 'object' ? base.custom_fields : {}
  const overlayCustom =
    overlay.custom_fields && typeof overlay.custom_fields === 'object' ? overlay.custom_fields : {}
  return {
    ...base,
    ...overlay,
    custom_fields: { ...baseCustom, ...overlayCustom },
  }
}

function isLoanRow(value: unknown): value is Loan {
  return Boolean(value && typeof value === 'object' && typeof (value as Loan).id === 'string')
}

/**
 * The same book StageRail / DealGrid already rendered.
 *
 * The bundled demo book is a fallback ONLY where `demo` says the account may see demo data. Without
 * that gate a live account that opened `?deal=AL-2025-001` was shown the demo deal ("One Liberty Plaza",
 * $245M) as if it were a record of its own, with the not-found banner suppressed.
 */
export function findPipelineLoan(
  queryClient: QueryClient,
  loanId: string,
  snapshot?: Loan | null,
  demo = false,
): Loan | undefined {
  const queries = queryClient.getQueriesData<unknown>({ queryKey: ['loans'] })
  for (const [, data] of queries) {
    if (!Array.isArray(data)) continue
    const found = data.find((row) => isLoanRow(row) && row.id === loanId)
    if (found) return found
  }
  if (snapshot?.id === loanId) return snapshot
  return demo ? COMMAND_CENTER_LOANS.find((row) => row.id === loanId) : undefined
}

export function findAllPipelineLoans(
  queryClient: QueryClient,
  snapshot?: Loan | null,
  demo = false,
): Loan[] {
  const queries = queryClient.getQueriesData<unknown>({ queryKey: ['loans'] })
  for (const [, data] of queries) {
    if (!Array.isArray(data) || data.length === 0) continue
    if (data.every(isLoanRow)) return data
  }
  if (snapshot) {
    const book = demo && COMMAND_CENTER_LOANS.some((row) => row.id === snapshot.id)
      ? COMMAND_CENTER_LOANS
      : [snapshot]
    return book
  }
  return demo ? COMMAND_CENTER_LOANS : []
}

export function dossierSourceBanner(err: unknown, hasPipelineRow: boolean): string | null {
  if (!err || !hasPipelineRow) return null
  const status = (err as { response?: { status?: number } })?.response?.status
  // The pipeline row already binds the Property Dossier template. A missing
  // deal file (404) or unreachable host is expected for book/demo rows — not
  // a defect to banner. Only surface auth / permission problems.
  if (status === 404 || status == null) return null
  if (status === 401) return 'Showing the pipeline record. Sign in to load a persisted deal file.'
  if (status === 403) {
    return 'Showing the pipeline record. This account cannot read the persisted deal file.'
  }
  return null
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(/[$,%\s]/g, ''))
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

/** Apply a snake_case deal-file patch onto the pipeline Loan the grid already holds. */
export function applySnakePatchToLoan(loan: Loan, patch: Record<string, unknown>): Loan {
  const next: Loan = {
    ...loan,
    customFields: { ...(loan.customFields ?? {}) },
    cashFlows: { ...loan.cashFlows },
    market: { ...loan.market },
  }
  const custom = next.customFields as Record<string, unknown>
  for (const [key, rawValue] of Object.entries(patch)) {
    if (key === 'custom_fields' && rawValue && typeof rawValue === 'object') {
      Object.assign(custom, rawValue as Record<string, unknown>)
      continue
    }
    switch (key) {
      case 'property_name':
        next.property = asText(rawValue)
        break
      case 'asset_type':
        next.type = asText(rawValue) as Loan['type']
        break
      case 'location':
        next.location = asText(rawValue)
        break
      case 'borrower':
        next.borrower = asText(rawValue)
        break
      case 'sponsor':
        next.sponsor = asText(rawValue)
        break
      case 'loan_amount': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.loanAmount = n
        break
      }
      case 'broker_name':
        next.brokerName = asText(rawValue)
        break
      case 'ltv': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.ltv = n
        break
      }
      case 'dscr': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.dscr = n
        break
      }
      case 'assignee':
        next.assignee = asText(rawValue)
        break
      case 'originated':
        next.originated = asText(rawValue)
        break
      case 'priority':
        next.priority = asText(rawValue) as Loan['priority']
        break
      case 'rate':
        next.rate = asText(rawValue)
        break
      case 'property_value': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.propertyValue = n
        break
      }
      case 'noi': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.noi = n
        break
      }
      case 'sf': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.sf = n
        break
      }
      case 'units': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.units = n
        break
      }
      case 'year_built': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.yearBuilt = n
        break
      }
      case 'debt_yield': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.debtYield = n
        break
      }
      case 'product':
      case 'capital_program':
        next.product = asText(rawValue) as Loan['product']
        break
      case 'cap_rate': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.market = { ...next.market, capRate: n }
        custom.cap_rate = rawValue
        break
      }
      case 'expense_ratio': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.cashFlows = { ...next.cashFlows, expenseRatio: n }
        custom.expense_ratio = rawValue
        break
      }
      case 'gross_revenue': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.cashFlows = { ...next.cashFlows, revenue: n }
        custom.gross_revenue = rawValue
        break
      }
      case 'operating_expenses': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.cashFlows = { ...next.cashFlows, expenses: n }
        custom.operating_expenses = rawValue
        break
      }
      case 'ncf': {
        const n = asNumber(rawValue)
        if (n !== undefined) next.cashFlows = { ...next.cashFlows, ncf: n }
        custom.ncf = rawValue
        break
      }
      case 'occupancy':
        custom.occupancy = rawValue
        break
      case 'purpose':
        custom.purpose = rawValue
        break
      case 'rent_roll':
        if (Array.isArray(rawValue)) {
          next.rentRoll = rawValue as Loan['rentRoll']
        }
        custom.rent_roll = rawValue
        break
      default:
        custom[key] = rawValue
        break
    }
  }
  next.customFields = custom
  return next
}

/** Keep the in-memory book and any cached loan lists in step with a saved row. */
export function writePipelineLoan(queryClient: QueryClient, loan: Loan) {
  const idx = COMMAND_CENTER_LOANS.findIndex((row) => row.id === loan.id)
  if (idx >= 0) COMMAND_CENTER_LOANS[idx] = loan
  const queries = queryClient.getQueriesData<unknown>({ queryKey: ['loans'] })
  for (const [key, data] of queries) {
    if (!Array.isArray(data)) continue
    queryClient.setQueryData(
      key,
      data.map((row) => (isLoanRow(row) && row.id === loan.id ? loan : row)),
    )
  }
}
